import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { SelectedAsset, ViewMode } from "@/store/assetsExplorer";
import { RtcTask } from "@/store/thread";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { useMemo } from "react";
import { AssetItem } from "./shared/AssetItem";

interface VideoTabProps {
  data: ArtifactsViewerData["videoStoryboard"]["data"];
  rtcTasks: RtcTask[];
  viewMode: ViewMode;
  selectedFilePaths: Set<string>;
  onSelect: (asset: SelectedAsset) => void;
  stopSignal?: number;
}

export function VideoTab(props: VideoTabProps) {
  const { data, viewMode, selectedFilePaths, onSelect, stopSignal } = props;

  // Get all video versions from shotsMetadatas for each shot
  const videos = useMemo(() => {
    const result: Array<{
      path: string;
      url: string;
      previewUrl: string;
      shotIndex: number;
    }> = [];

    data.shots.forEach((shot, index) => {
      const shotIndex = shot.shot_index;
      const metadata = data.shotsMetadatas[index];
      const previewUrl = shot.start_frame_image_file_path
        ? convertFilePathToUrl(shot.start_frame_image_file_path, {
            level: "medium",
          })
        : "";

      if (metadata?.video_clip?.versions) {
        const versions = metadata.video_clip.versions;
        [...versions].reverse().forEach((version) => {
          if (version.file_path) {
            result.push({
              path: version.file_path,
              url: convertFilePathToUrl(version.file_path),
              previewUrl,
              shotIndex,
            });
          }
        });
      }
    });

    return result;
  }, [data.shots, data.shotsMetadatas]);

  // Always call useMemo for shotGroups (Hooks must be called in the same order)
  const shotGroups = useMemo(() => {
    const groups: Record<number, typeof videos> = {};
    videos.forEach((video) => {
      const key = video.shotIndex;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(video);
    });
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [videos]);

  if (videos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary">
        No videos available
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {videos.map((video, index) => (
          <AssetItem
            key={`video_list_${video.path}_${index}`}
            src={convertFilePathToUrl(video.path, { level: "medium" })}
            previewSrc={video.url}
            alt={`Shot ${video.shotIndex}`}
            mediaType="video"
            index={video.shotIndex}
            onClick={() => onSelect({ type: "video", path: video.path })}
            stopSignal={stopSignal}
          />
        ))}
      </div>
    );
  }

  // Shots List view - group by shot

  return (
    <div className="flex flex-col gap-8">
      {shotGroups.map(([shotIndex, shotVideos]) => (
        <div key={shotIndex}>
          <h3 className="text-text-primary text-base font-bold mb-4">
            Shot {Number(shotIndex) + 1}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {shotVideos.map((video, index) => (
              <AssetItem
                key={`video_list_${video.path}_${index}`}
                src={video.url}
                previewSrc={video.previewUrl}
                alt={`Shot ${video.shotIndex}`}
                mediaType="video"
                isSelected={selectedFilePaths.has(video.path)}
                onClick={() => onSelect({ type: "video", path: video.path })}
                stopSignal={stopSignal}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
